// The attachment row's stylesheet (popoverFiles.css): comment files render the shared FileRow.
import '@overlayqa/shared/ui/styles.css';
import { ComposerActions } from '@overlayqa/shared/ui';
/**
 * One comment row: the header (avatar, author, age, edited marker, Figma
 * badge), the own-comment actions menu, the edit form or the rendered body.
 * Extracted from CommentThread.tsx (refactor phase C3,
 * docs/refactoring/plans/2026-09-08-commentthread.md). The markup is the
 * parent's former map body verbatim; every piece of state it read became a
 * prop, every handler a callback the parent closes over the comment, so the
 * thread stays the one owner of menuOpenId / editingId / editContent /
 * deletingId. The prop-forwarding contract in
 * __tests__/CommentThread.commentItem.test.tsx pins each expression.
 *
 * Two rules kept exactly as they were: the shared menuRef attaches only to
 * the row whose menu is open (the click-outside effect reads it), and
 * EVERY row's Delete disables while any delete is in flight
 * (`deletingId !== null`), the label switching only on the row being
 * deleted.
 */
import { useRef, type ChangeEvent } from 'react';
import type React from 'react';
import { CommentAttachmentList, CommentEmojiPicker, type VideoAttachmentEvent } from '@overlayqa/shared/ui';
import { commentVisibilityLabel, type IssueComment, type MentionableUser } from '@overlayqa/shared';
import { Avatar } from '../ui/Avatar';
import { timeAgo } from '../../lib/utils';
import { renderContent } from './commentMentions';

export interface CommentItemProps {
  comment: IssueComment;
  onDownload?: (id: string) => Promise<void>;
  /** The authorised read behind onDownload, so a recording plays in place (shared CommentAttachmentList). */
  fetchFile?: (id: string) => Promise<{ base64: string }>;
  onVideoEvent?: (event: VideoAttachmentEvent) => void;
  /** A native comment by the current user: the only kind that gets the actions menu. */
  isOwn: boolean;
  teamMembers: MentionableUser[];
  /** Whether THIS row's actions menu is open; the shared menuRef attaches only then. */
  menuOpen: boolean;
  menuRef: React.RefObject<HTMLDivElement>;
  onToggleMenu: () => void;
  onStartEdit: () => void;
  onDelete: () => void;
  /** Id of the comment whose delete is in flight, thread-wide: every row's Delete disables. */
  deletingId: string | null;
  editing: boolean;
  editContent: string;
  onEditContentChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSaveEdit: () => void;
  isSaving: boolean;
  onCancelEdit: () => void;
}

export function CommentItem({
  comment,
  onDownload,
  fetchFile,
  onVideoEvent,
  isOwn,
  teamMembers,
  menuOpen,
  menuRef,
  onToggleMenu,
  onStartEdit,
  onDelete,
  deletingId,
  editing,
  editContent,
  onEditContentChange,
  onSaveEdit,
  isSaving,
  onCancelEdit,
}: CommentItemProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Markup mirrors team review's CommentCard
  // (packages/server/src/review-app/components/comments/CommentCard.tsx):
  // white card, 26px avatar, name then age, "Edited", visibility as a role
  // badge, ellipsis icon menu, 13px body. Keep the two in step.
  return (
    <article className={`flex flex-col gap-2 bg-white rounded-lg px-3 py-3 border border-gray-200 ${deletingId === comment.id ? 'opacity-50 pointer-events-none' : ''}`}>
      <header className="flex items-center gap-2">
        <Avatar id={comment.authorId} src={comment.authorAvatarUrl} name={comment.authorName} size="sm" className="!w-[26px] !h-[26px] shrink-0" />
        <div className="flex flex-1 min-w-0 flex-wrap items-center gap-x-2 gap-y-2">
          <span className="text-xs font-semibold text-gray-900 break-words">{comment.authorName}</span>
          <span className="text-xs text-gray-500">{timeAgo(comment.createdAt)}</span>
          {comment.editedAt && <span className="text-xs text-gray-400 italic">Edited</span>}
          {comment.source === 'native' && (
            <span
              className={`inline-flex items-center whitespace-nowrap rounded px-2 text-[10px] font-medium leading-4 border ${comment.visibility === 'public' ? 'bg-[var(--color-primary-light)] text-primary-700 border-primary-200' : 'bg-green-50 text-green-700 border-green-200'}`}
              aria-label={`Visible to ${commentVisibilityLabel(comment.visibility)}`}
            >
              {commentVisibilityLabel(comment.visibility)}
            </span>
          )}
        </div>
        {comment.source === 'figma' && (
          <span className="inline-flex items-center h-[18px] px-[5px] rounded text-[10px] font-medium bg-gray-100 text-gray-600 whitespace-nowrap shrink-0">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="mr-[3px] shrink-0"><polygon points="12 2 22 12 12 22 2 12" /></svg>
            Figma
          </span>
        )}
        {isOwn && (
          <div className="relative shrink-0" ref={menuOpen ? menuRef : undefined}>
            <button
              type="button"
              className="flex items-center justify-center w-6 h-6 rounded text-gray-500 hover:bg-gray-100"
              onClick={onToggleMenu}
              aria-label={`Actions for comment by ${comment.authorName}`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
            </button>
            {menuOpen && (
              <div role="menu" className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-md z-10 min-w-[100px] overflow-hidden">
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full text-left px-3 py-[7px] text-sm text-gray-900 hover:bg-gray-50"
                  onClick={onStartEdit}
                >
                  <span className="text-cap-trim block">Edit</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full text-left px-3 py-[7px] text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={onDelete}
                  disabled={deletingId !== null}
                >
                  <span className="text-cap-trim block">{deletingId === comment.id ? 'Deleting...' : 'Delete'}</span>
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      {editing ? (
        <div className="flex flex-col gap-2">
          <textarea
            ref={textareaRef}
            aria-label="Edit comment"
            className="w-full border border-gray-200 rounded-md p-2 text-sm resize-none focus:border-primary-500 focus:ring-1 focus:ring-primary-200 outline-none"
            value={editContent}
            onChange={onEditContentChange}
            rows={2}
          />
          <ComposerActions>
            <CommentEmojiPicker textareaRef={textareaRef} disabled={isSaving} onChange={value => onEditContentChange({ target: { value } } as ChangeEvent<HTMLTextAreaElement>)} />
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={onCancelEdit}
            >
              <span className="text-cap-trim">Cancel</span>
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm disabled:bg-gray-200 disabled:text-gray-400"
              onClick={onSaveEdit}
              disabled={isSaving || (!editContent.trim() && !comment.attachments?.length)}
            >
              <span className="text-cap-trim">{isSaving ? 'Saving...' : 'Save comment'}</span>
            </button>
          </ComposerActions>
        </div>
      ) : (
        <div className="prose-measure text-sm text-gray-900 leading-normal break-words">
          {renderContent(comment.content, teamMembers)}
        </div>
      )}
      {onDownload && <CommentAttachmentList files={comment.attachments} onDownload={onDownload} fetchFile={fetchFile} onVideoEvent={onVideoEvent} />}
    </article>
  );
}
